/**
 * @kora/web API client.
 *
 * Thin wrapper around `fetch` that handles three things the raw fetch API
 * doesn't:
 *
 * 1. **Bearer token injection.** The current access token lives in memory
 *    (see ../auth/tokenStore.ts — NOT localStorage, XSS-safe). Every
 *    request picks it up from the store.
 *
 * 2. **Silent refresh on 401.** If a request returns 401, we call
 *    POST /v1/auth/refresh (which uses the httpOnly refresh cookie —
 *    the browser sends it automatically). If refresh succeeds, we swap
 *    in the new access token and retry the original request exactly once.
 *    If refresh fails, we clear the store and surface the 401.
 *
 * 3. **Refresh thundering-herd guard.** If N concurrent requests all hit
 *    401 at the same time, only one refresh call fires — the rest await
 *    the same promise via `inFlightRefresh`.
 *
 * The API base URL is resolved from VITE_API_BASE_URL at build time;
 * defaults to same-origin (useful for Vite's dev proxy).
 */

import { getAccessToken, setAccessToken, clearAccessToken } from '../auth/tokenStore.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

// -------------------------------------------------------------------------
// Error type
// -------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// -------------------------------------------------------------------------
// Public surface
// -------------------------------------------------------------------------

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  headers?: Record<string, string>;
  /** When true, skip the Bearer header. Used by /v1/auth/* endpoints. */
  unauthenticated?: boolean;
  /**
   * When true, skip the 401-refresh-retry cycle. Used by the refresh
   * endpoint itself to avoid infinite recursion, and by /v1/auth/login
   * where 401 is a real "bad credentials" signal, not an expired token.
   */
  skipRefresh?: boolean;
}

export async function apiRequest<T = unknown>(path: string, req: ApiRequest = {}): Promise<T> {
  const res = await doFetch(path, req);
  if (res.status === 401 && !req.skipRefresh && !req.unauthenticated) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const retry = await doFetch(path, req);
      return parseResponse<T>(retry);
    }
    clearAccessToken();
  }
  return parseResponse<T>(res);
}

/**
 * GET a binary response with auth headers and trigger a browser download.
 * Used for PDFs (payslips), CSV exports, etc — anything where
 * `apiRequest`'s JSON-parsing isn't appropriate.
 *
 * Mirrors the apiRequest 401-refresh dance so a stale access token
 * doesn't fail the download silently.
 *
 * Returns nothing — the side effect is the browser save-dialog. Throws
 * `ApiError` on non-2xx responses (after refresh attempt).
 */
export async function apiDownload(
  path: string,
  fallbackFilename = 'download',
): Promise<void> {
  let res = await doFetch(path, { method: 'GET' });
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await doFetch(path, { method: 'GET' });
    } else {
      clearAccessToken();
    }
  }
  if (!res.ok) {
    // Try to surface a JSON error body if the server sent one;
    // otherwise we just have the status.
    const contentType = res.headers.get('content-type') ?? '';
    const body = contentType.includes('application/json')
      ? await res.json().catch(() => null)
      : null;
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  const blob = await res.blob();
  const filename =
    parseFilenameFromContentDisposition(res.headers.get('content-disposition')) ??
    fallbackFilename;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Firefox needs the link to be in the DOM for the click to register.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Free the object URL on the next tick — some browsers race the
  // download trigger with revoke if we revoke synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/**
 * Authenticated binary fetch that returns the bytes + filename without
 * triggering the browser save dialog. The caller decides what to do
 * with the Blob (create an object URL, render inline, hand to
 * download helper, etc).
 *
 * Mirrors `apiDownload`'s auth + 401-refresh dance — stale tokens
 * don't fail the fetch silently. Differs in two ways:
 *   - returns `{ blob, filename }` rather than triggering download
 *   - throws an ApiError on non-2xx (consistent with `apiRequest`)
 *
 * Used by /me/documents/:id/download — the route component creates
 * a URL.createObjectURL + clicks a hidden anchor + revokes the URL.
 * That pattern works under pure-JWT auth; the older admin pattern
 * (open href in new tab) depends on the refresh cookie being sent
 * with browser-initiated navigation, which is a hidden coupling.
 *
 * ADR-0034 added the optional third argument (`ApiDownloadOptions`). It is
 * **purely additive** — omit it and this behaves exactly as it did before:
 * GET, no body, refresh-and-retry on 401.
 */
export async function apiDownloadBlob(
  path: string,
  fallbackFilename = 'download',
  opts: ApiDownloadOptions = {},
): Promise<{ blob: Blob; filename: string }> {
  // Defaults reproduce the previous behaviour exactly: GET, no body, refresh
  // on 401. See ApiDownloadOptions.
  const req: ApiRequest = {
    method: opts.method ?? 'GET',
    ...(opts.body !== undefined ? { body: opts.body } : {}),
  };
  let res = await doFetch(path, req);
  if (res.status === 401 && !opts.skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await doFetch(path, req);
    } else {
      clearAccessToken();
    }
  }
  if (!res.ok) {
    const contentType = res.headers.get('content-type') ?? '';
    const body = contentType.includes('application/json')
      ? await res.json().catch(() => null)
      : null;
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  const blob = await res.blob();
  const filename =
    parseFilenameFromContentDisposition(res.headers.get('content-disposition')) ??
    fallbackFilename;
  return { blob, filename };
}

/**
 * Options for the download helpers. **Every field is optional and every
 * default reproduces the pre-ADR-0034 behaviour byte-for-byte** — an existing
 * caller that passes nothing gets `GET`, no body, and the 401-refresh-retry it
 * has always had. The capability is additive; the contract did not move.
 *
 * Added for `POST /v1/export/me` (ADR-0034), the first download in the app that
 * needs a request **body** — because a re-auth password cannot travel in a URL.
 */
export interface ApiDownloadOptions {
  /** Defaults to `'GET'`. */
  method?: 'GET' | 'POST';
  /** JSON-serialized when present. Defaults to no body. */
  body?: unknown;
  /**
   * Same meaning as `ApiRequest.skipRefresh`: **this endpoint's 401 is a
   * credential rejection, not a stale access token**, so do not refresh and
   * retry. Defaults to `false`.
   *
   * ⚠️ **Not a nicety — without it a re-auth download misbehaves twice.** On a
   * wrong password the export returns 401; the refresh-retry path would then
   * (1) **re-send the same wrong password**, doubling the failed attempt, and
   * (2) if the refresh itself failed, `clearAccessToken()` — **signing the user
   * out because they made a typo**, then reporting it as a bad password. The
   * caller that knows 401 means "wrong password" passes this.
   */
  skipRefresh?: boolean;
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // Match `filename="..."` or `filename=...` (no quotes). RFC 6266
  // also defines `filename*=UTF-8''<encoded>`; we support that too.
  const utfMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      // fall through to plain match
    }
  }
  const plainMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plainMatch?.[1]?.trim() ?? null;
}

// -------------------------------------------------------------------------
// Internals
// -------------------------------------------------------------------------

async function doFetch(path: string, req: ApiRequest): Promise<Response> {
  const isFormData =
    typeof FormData !== 'undefined' && req.body instanceof FormData;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(req.headers ?? {}),
  };
  // For JSON bodies, set Content-Type. For FormData, DON'T — the
  // browser computes the right multipart/form-data; boundary=...
  // header itself, and overriding it strips the boundary which
  // makes the server unable to parse the upload.
  if (req.body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  if (!req.unauthenticated) {
    const token = getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let serializedBody: BodyInit | undefined;
  if (req.body !== undefined) {
    serializedBody = isFormData ? (req.body as FormData) : JSON.stringify(req.body);
  }

  return fetch(`${API_BASE_URL}${path}`, {
    method: req.method ?? 'GET',
    headers,
    credentials: 'include', // required for refresh cookie
    ...(serializedBody !== undefined ? { body: serializedBody } : {}),
  });
}

async function parseResponse<T>(res: Response): Promise<T> {
  // 204 No Content — no body to parse
  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message =
      (isJson && body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText) || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

// -------------------------------------------------------------------------
// Refresh singleton — coalesces concurrent 401s into one refresh call
// -------------------------------------------------------------------------

let inFlightRefresh: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken?: string };
      if (!body.accessToken) return false;
      setAccessToken(body.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

/** Test-only: reset the in-flight promise so tests can isolate. */
export function __resetRefreshStateForTests(): void {
  inFlightRefresh = null;
}
